<template>
  <main class="operations-page">
    <header class="page-header">
      <div>
        <p>MARKETPLACE OPERATIONS</p>
        <span>围绕审核、人工编排与有效激活，管理能力从发布到使用的完整闭环。</span>
      </div>
      <a-button :loading="loading" @click="load"><i class="ri-refresh-line" />刷新数据</a-button>
    </header>
    <a-alert v-if="loadError" type="error" show-icon :message="loadError" class="error" />
    <a-spin :spinning="loading">
      <section class="funnel">
        <article v-for="item in funnelCards" :key="item.key">
          <span>{{ item.label }}</span
          ><strong>{{ item.value }}</strong
          ><small>{{ item.note }}</small>
        </article>
      </section>
      <a-tabs v-model:active-key="tab" class="marketplace-tabs">
        <a-tab-pane key="reviews" tab="审核队列"
          ><section class="panel">
            <div class="panel-head">
              <div>
                <h2>待审核发布</h2>
                <p>审核绑定不可变版本；拒绝新版本不会影响当前已经上线的版本。</p>
              </div>
              <a-tag color="orange">{{ pendingReviews.length }} 待处理</a-tag>
            </div>
            <a-empty v-if="!pendingReviews.length" description="当前没有待审核资产" /><a-table
              v-else
              :data-source="pendingReviews"
              :columns="reviewColumns"
              :pagination="false"
              row-key="id"
              ><template #bodyCell="{ column, record }"
                ><template v-if="column.key === 'asset'"
                  ><strong>{{ record.package?.name || record.packageId }}</strong
                  ><small
                    >{{
                      record.release?.version ? `v${record.release.version}` : record.releaseId
                    }}
                    · {{ typeLabel(record.package?.type) }}</small
                  ></template
                ><template v-else-if="column.key === 'submittedAt'">{{
                  date(record.submittedAt)
                }}</template
                ><template v-else-if="column.key === 'action'"
                  ><a-space
                    ><a-button size="small" @click="openReviewDetail(record)">审核详情</a-button
                    ><a-button type="primary" size="small" @click="openReview(record, 'approved')"
                      >通过</a-button
                    ><a-button danger size="small" @click="openReview(record, 'rejected')"
                      >拒绝</a-button
                    ></a-space
                  ></template
                ></template
              ></a-table
            >
          </section></a-tab-pane
        >
        <a-tab-pane key="ratings" tab="用户评价">
          <section class="panel">
            <div class="panel-head">
              <div>
                <h2>待审核评价</h2>
                <p>通过后才会公开展示并计入商店评分；评价者在商店详情可见自己的审核状态。</p>
              </div>
              <a-tag color="orange">{{ pendingRatings.length }} 待处理</a-tag>
            </div>
            <a-empty v-if="!pendingRatings.length" description="当前没有待审核评价" />
            <a-table
              v-else
              :data-source="pendingRatings"
              :columns="ratingColumns"
              :pagination="false"
              row-key="id"
            >
              <template #bodyCell="{ column, record }">
                <template v-if="column.key === 'author'">
                  <strong>{{ record.author.displayName }}</strong
                  ><small>{{ record.packageId }}</small>
                </template>
                <template v-else-if="column.key === 'rating'"
                  ><a-rate :value="record.rating" disabled
                /></template>
                <template v-else-if="column.key === 'comment'">{{
                  record.comment || '仅评分，未填写文字评价'
                }}</template>
                <template v-else-if="column.key === 'submittedAt'">{{
                  date(record.updatedAt)
                }}</template>
                <template v-else-if="column.key === 'action'"
                  ><a-space
                    ><a-button type="primary" size="small" @click="reviewRating(record, 'approved')"
                      >通过</a-button
                    ><a-button danger size="small" @click="reviewRating(record, 'rejected')"
                      >拒绝</a-button
                    ></a-space
                  ></template
                >
              </template>
            </a-table>
          </section>
        </a-tab-pane>
        <a-tab-pane key="demands" tab="需求审核">
          <section class="panel">
            <div class="panel-head">
              <div>
                <h2>待审核需求</h2>
                <p>通过后公开到需求广场；需求修改会重新进入本队列。</p>
              </div>
              <a-tag color="orange">{{ pendingDemands.length }} 待处理</a-tag>
            </div>
            <a-empty v-if="!pendingDemands.length" description="当前没有待审核需求" />
            <a-table
              v-else
              :data-source="pendingDemands"
              :columns="demandColumns"
              :pagination="false"
              row-key="id"
            >
              <template #bodyCell="{ column, record }">
                <template v-if="column.key === 'demand'"
                  ><strong>{{ record.title }}</strong
                  ><small
                    ><a-button
                      type="link"
                      size="small"
                      class="user-link"
                      @click.stop="openUserDetail(record.author)"
                      >{{ record.author.displayName }}</a-button
                    >
                    · {{ record.responseCount }} 个方案</small
                  ></template
                >
                <template v-else-if="column.key === 'submittedAt'">{{
                  date(record.updatedAt)
                }}</template>
                <template v-else-if="column.key === 'action'"
                  ><a-space
                    ><a-button size="small" @click="openDemandDetail(record.id)">查看</a-button
                    ><a-button type="primary" size="small" @click="reviewDemand(record, 'approved')"
                      >通过</a-button
                    ><a-button danger size="small" @click="reviewDemand(record, 'rejected')"
                      >拒绝</a-button
                    ></a-space
                  ></template
                >
              </template>
            </a-table>
          </section>
        </a-tab-pane>
        <a-tab-pane key="curation" tab="运营编排"
          ><section class="panel">
            <div class="panel-head">
              <div>
                <h2>首页专题</h2>
                <p>主推荐位同时只能有一个生效；专题仅可引用已审核通过的在线资产。</p>
              </div>
              <a-button type="primary" @click="collectionOpen = true"
                ><i class="ri-add-line" />新建专题</a-button
              >
            </div>
            <a-table
              :data-source="collections"
              :columns="collectionColumns"
              :pagination="false"
              row-key="id"
              ><template #bodyCell="{ column, record }"
                ><template v-if="column.key === 'status'"
                  ><a-tag
                    :color="
                      record.status === 'live'
                        ? 'green'
                        : record.status === 'draft'
                          ? 'default'
                          : 'blue'
                    "
                    >{{ collectionStatus(record.status) }}</a-tag
                  ></template
                ><template v-else-if="column.key === 'schedule'"
                  >{{ record.startsAt ? date(record.startsAt) : '立即' }} —
                  {{ record.endsAt ? date(record.endsAt) : '长期' }}</template
                ><template v-else-if="column.key === 'assets'"
                  >{{ record.packageIds.length }} 个资产</template
                ></template
              ></a-table
            >
          </section></a-tab-pane
        >
        <a-tab-pane key="assets" tab="资产管理"
          ><section class="panel">
            <div class="panel-head">
              <div>
                <h2>商店资产</h2>
                <p>下架不会影响已有用户；删除只移除商店发布记录，已获取版本仍可使用和卸载。</p>
              </div>
            </div>
            <div class="asset-filters">
              <a-input-search
                v-model:value="assetQuery"
                class="asset-search"
                allow-clear
                placeholder="搜索名称、描述或标签"
                @search="resetAssetPage"
              />
              <a-select v-model:value="assetType" class="asset-filter" aria-label="资产类型">
                <a-select-option value="">全部类型</a-select-option
                ><a-select-option value="component">组件</a-select-option
                ><a-select-option value="skill">Skill</a-select-option
                ><a-select-option value="mcp">MCP</a-select-option
                ><a-select-option value="agent_template">Agent 模板</a-select-option>
              </a-select>
              <a-select v-model:value="assetVisibility" class="asset-filter" aria-label="上架状态">
                <a-select-option value="">全部上架状态</a-select-option
                ><a-select-option value="public">在线</a-select-option
                ><a-select-option value="private">已下架</a-select-option
                ><a-select-option value="link">链接可见</a-select-option>
              </a-select>
            </div>
            <a-table
              :data-source="assets"
              :columns="assetColumns"
              :pagination="assetPagination"
              row-key="id"
              @change="handleAssetTableChange"
              ><template #bodyCell="{ column, record }"
                ><template v-if="column.key === 'asset'"
                  ><a-button type="link" class="asset-name" @click="openAssetDetail(record)">{{
                    record.name
                  }}</a-button
                  ><small
                    >v{{ record.latestVersion || '未发布' }} · {{ record.id }}</small
                  ></template
                ><template v-else-if="column.key === 'status'"
                  ><a-tag
                    :color="
                      record.visibility === 'public'
                        ? 'green'
                        : record.visibility === 'link'
                          ? 'blue'
                          : 'default'
                    "
                    >{{
                      record.visibility === 'public'
                        ? '在线'
                        : record.visibility === 'link'
                          ? '链接可见'
                          : '已下架'
                    }}</a-tag
                  ></template
                ><template v-else-if="column.key === 'review'"
                  ><a-tag
                    :color="
                      record.reviewStatus === 'approved'
                        ? 'green'
                        : record.reviewStatus === 'pending'
                          ? 'orange'
                          : 'default'
                    "
                    >{{ reviewStatus(record.reviewStatus) }}</a-tag
                  ></template
                ><template v-else-if="column.key === 'priority'"
                  ><a-space
                    ><a-input-number
                      :value="record.editorialPriority || 0"
                      :min="0"
                      :max="20"
                      size="small"
                      :disabled="
                        record.visibility !== 'public' || record.reviewStatus !== 'approved'
                      "
                      @change="(value: unknown) => updatePriority(record.id, Number(value || 0))"
                    /><span>/ 20</span></a-space
                  ></template
                ><template v-else-if="column.key === 'action'"
                  ><a-space
                    ><a-button size="small" @click="openAssetDetail(record)">详情</a-button
                    ><a-button
                      v-if="record.visibility === 'public'"
                      danger
                      size="small"
                      @click="openForceUnpublish(record)"
                      >强制下架</a-button
                    ><a-button v-else danger size="small" @click="openAssetDelete(record)"
                      >删除</a-button
                    ></a-space
                  ></template
                ></template
              ></a-table
            >
          </section></a-tab-pane
        >
        <a-tab-pane key="analytics" tab="激活漏斗"
          ><section class="panel">
            <div class="panel-head">
              <div>
                <h2>有效激活</h2>
                <p>以能力真正可用或组件首次被实例化作为结果信号，不将单纯浏览计为成功。</p>
              </div>
            </div>
            <a-table
              :data-source="analytics?.topPackages || []"
              :columns="topColumns"
              :pagination="false"
              row-key="packageId"
              ><template #bodyCell="{ column, record }"
                ><template v-if="column.key === 'type'">{{
                  typeLabel(record.type)
                }}</template></template
              ></a-table
            >
          </section></a-tab-pane
        >
      </a-tabs>
    </a-spin>
  </main>

  <a-modal
    v-model:open="reviewOpen"
    title="拒绝上架"
    :confirm-loading="submitting"
    :ok-button-props="{ danger: true, disabled: !reason.trim() }"
    ok-text="确认拒绝"
    @ok="submitReview"
    ><p>拒绝原因会反馈给发布者并写入审计记录。</p>
    <a-textarea
      v-model:value="reason"
      :rows="4"
      :maxlength="200"
      show-count
      placeholder="请说明拒绝原因"
  /></a-modal>
  <a-modal
    v-model:open="ratingReviewOpen"
    title="拒绝公开评价"
    :confirm-loading="submitting"
    :ok-button-props="{ danger: true, disabled: !ratingReviewReason.trim() }"
    ok-text="确认拒绝"
    @ok="submitRatingReview"
    ><p>该评价不会公开展示或计入评分，操作会写入管理员审计日志。</p>
    <a-textarea
      v-model:value="ratingReviewReason"
      :rows="4"
      :maxlength="200"
      show-count
      placeholder="请说明拒绝原因"
  /></a-modal>
  <a-modal
    v-model:open="demandReviewOpen"
    title="拒绝需求发布"
    :confirm-loading="submitting"
    :ok-button-props="{ danger: true, disabled: !demandReason.trim() }"
    ok-text="确认拒绝"
    @ok="submitDemandReview"
    ><p>驳回原因会反馈给需求发布者并写入审计记录。</p>
    <a-textarea
      v-model:value="demandReason"
      :rows="4"
      :maxlength="500"
      show-count
      placeholder="请说明驳回原因"
  /></a-modal>
  <a-modal v-model:open="demandDetailOpen" title="需求与方案" :footer="null" width="760px"
    ><template v-if="demandDetail"
      ><h3>{{ demandDetail.title }}</h3>
      <p class="demand-detail-copy">{{ demandDetail.description }}</p>
      <h4>期望结果</h4>
      <p class="demand-detail-copy">{{ demandDetail.expectedOutcome }}</p>
      <h4>方案响应</h4>
      <a-empty v-if="!demandDetail.responses.length" :image="false" description="暂无方案响应" />
      <div v-else class="demand-response-list">
        <article v-for="item in demandDetail.responses" :key="item.id">
          <a-button
            type="link"
            size="small"
            class="user-link"
            @click="openUserDetail(item.author)"
            >{{ item.author.displayName }}</a-button
          ><a-tag v-if="item.status !== 'visible'" color="default">{{
            item.status === 'hidden' ? '已隐藏' : '已撤回'
          }}</a-tag>
          <p>{{ item.summary }}</p>
          <a-button
            v-if="item.status === 'visible'"
            danger
            size="small"
            @click="openHideResponse(item.id)"
            >隐藏响应</a-button
          >
        </article>
      </div></template
    ></a-modal
  >
  <a-modal
    v-model:open="demandHideOpen"
    title="隐藏需求响应"
    :confirm-loading="submitting"
    :ok-button-props="{ danger: true, disabled: !demandHideReason.trim() }"
    ok-text="确认隐藏"
    @ok="hideDemandResponse"
    ><p>隐藏后会通知响应者，并自动取消该方案的推荐状态。</p>
    <a-textarea
      v-model:value="demandHideReason"
      :rows="4"
      :maxlength="500"
      show-count
      placeholder="请说明隐藏原因"
  /></a-modal>
  <a-modal
    v-model:open="forceUnpublishOpen"
    title="强制下架资产"
    :confirm-loading="submitting"
    :ok-button-props="{ danger: true, disabled: !forceUnpublishReason.trim() }"
    ok-text="确认强制下架"
    @ok="forceUnpublish"
    ><p>资产将立即从商店隐藏，新的发现与安装会被阻断。此操作会写入管理员审计日志。</p>
    <a-textarea
      v-model:value="forceUnpublishReason"
      :rows="4"
      :maxlength="200"
      show-count
      placeholder="请填写强制下架原因"
  /></a-modal>
  <a-modal
    v-model:open="assetDeleteOpen"
    title="永久删除商店资产"
    :confirm-loading="submitting"
    :ok-button-props="{
      danger: true,
      disabled: !assetDeleteReason.trim() || assetDeleteName !== assetDeleteTarget?.name,
    }"
    ok-text="确认永久删除"
    @ok="deleteAsset"
    ><template v-if="assetDeleteImpact"
      ><a-alert
        type="warning"
        show-icon
        message="删除只移除能力商店记录，已获取的用户版本仍可使用和卸载，但无法更新、重新加入或重新安装。"
      />
      <p class="delete-impact">
        {{ assetDeleteImpact.libraryCount }} 个个人资产库、{{ assetDeleteImpact.installationCount }}
        个工作台安装将保留。
      </p></template
    ><a-form layout="vertical"
      ><a-form-item label="删除原因" required
        ><a-textarea
          v-model:value="assetDeleteReason"
          :maxlength="200"
          :rows="3"
          show-count /></a-form-item
      ><a-form-item :label="`输入“${assetDeleteTarget?.name ?? ''}”确认`" required
        ><a-input v-model:value="assetDeleteName" /></a-form-item></a-form
  ></a-modal>
  <a-drawer v-model:open="assetDetailOpen" title="资产详情" placement="right" :width="760">
    <a-spin :spinning="assetDetailLoading"
      ><template v-if="assetDetail"
        ><section class="asset-detail-head">
          <img
            v-if="assetDetail.asset.iconImage"
            :src="assetDetail.asset.iconImage"
            :alt="assetDetail.asset.name"
          />
          <div>
            <h2>{{ assetDetail.asset.name }}</h2>
            <p>
              {{ typeLabel(assetDetail.asset.type) }} ·
              {{
                assetDetail.asset.latestVersion
                  ? `v${assetDetail.asset.latestVersion}`
                  : '未发布版本'
              }}
            </p>
          </div>
        </section>
        <a-descriptions bordered size="small" :column="1"
          ><a-descriptions-item label="发布者">{{
            assetDetail.publisher?.displayName ?? '已删除用户'
          }}</a-descriptions-item
          ><a-descriptions-item label="状态"
            >{{ assetDetail.asset.visibility === 'public' ? '在线' : '已下架' }} ·
            {{ reviewStatus(assetDetail.asset.reviewStatus) }}</a-descriptions-item
          ><a-descriptions-item label="分类与标签"
            >{{ assetDetail.asset.category ?? 'other'
            }}{{
              assetDetail.asset.tags.length ? ` · ${assetDetail.asset.tags.join('、')}` : ''
            }}</a-descriptions-item
          ><a-descriptions-item label="用户持有 / 安装"
            >{{ assetDetail.metrics.libraryCount }} /
            {{ assetDetail.metrics.installationReadyCount }}</a-descriptions-item
          ><a-descriptions-item label="资产 ID"
            ><code>{{ assetDetail.asset.id }}</code></a-descriptions-item
          ></a-descriptions
        >
        <section class="asset-detail-section">
          <h3>简介</h3>
          <p>{{ assetDetail.asset.description || '未填写简介' }}</p>
        </section>
        <section v-if="assetDetail.release" class="asset-detail-section">
          <h3>发布版本</h3>
          <p>
            v{{ assetDetail.release.version }} ·
            {{ assetDetail.release.changelog || '未填写更新说明' }}
          </p>
          <a-space wrap
            ><a-tag v-for="item in assetDetail.release.dependencies" :key="item.alias"
              >{{ item.alias }} · {{ item.versionRange }}</a-tag
            ><a-tag v-if="!assetDetail.release.dependencies.length">无依赖</a-tag></a-space
          >
          <div v-if="assetDetail.release.screenshots?.length" class="asset-screenshots">
            <img
              v-for="item in assetDetail.release.screenshots"
              :key="item"
              :src="item"
              alt="资产截图"
            />
          </div>
        </section>
        <a-alert
          v-if="assetDetail.asset.type === 'component' && !assetDetail.review"
          type="info"
          show-icon
          message="当前组件没有可用审核会话，因此无法打开隔离预览。"
        /><a-button
          v-else-if="assetDetail.asset.type === 'component' && assetDetail.review"
          type="primary"
          @click="openAssetPreview"
          ><i class="ri-play-line" />隔离预览组件</a-button
        ></template
      ></a-spin
    >
  </a-drawer>
  <a-modal
    v-model:open="collectionOpen"
    title="新建首页专题"
    :confirm-loading="submitting"
    :ok-button-props="{
      disabled:
        !collectionDraft.title.trim() ||
        !collectionDraft.packageIds.trim() ||
        !collectionDraft.reason.trim(),
    }"
    ok-text="保存专题"
    @ok="saveCollection"
    ><a-form layout="vertical"
      ><a-form-item label="标题" required
        ><a-input v-model:value="collectionDraft.title" :maxlength="80" /></a-form-item
      ><a-form-item label="说明"
        ><a-textarea v-model:value="collectionDraft.description" :maxlength="240" /></a-form-item
      ><a-form-item label="位置"
        ><a-radio-group v-model:value="collectionDraft.placement"
          ><a-radio value="hero">主推荐</a-radio><a-radio value="featured">精选</a-radio
          ><a-radio value="scenario">场景</a-radio></a-radio-group
        ></a-form-item
      ><a-form-item label="状态"
        ><a-radio-group v-model:value="collectionDraft.status"
          ><a-radio value="draft">草稿</a-radio
          ><a-radio value="live">立即生效</a-radio></a-radio-group
        ></a-form-item
      ><a-form-item label="资产 ID" required
        ><a-textarea
          v-model:value="collectionDraft.packageIds"
          placeholder="每行一个已审核通过的 packageId"
          :rows="4" /></a-form-item
      ><a-form-item label="操作原因" required
        ><a-input v-model:value="collectionDraft.reason" :maxlength="200" /></a-form-item></a-form
  ></a-modal>
  <MarketplaceReviewWorkbench
    v-model:open="workbenchOpen"
    :review="workbenchTarget"
    @changed="load"
  />
  <MarketplaceAssetReviewDetail
    v-model:open="assetReviewDetailOpen"
    :review="assetReviewDetailTarget"
    @changed="load"
  />
  <AdminUserDetailModal v-model:open="userDetailOpen" :user="userDetailTarget" />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import { useRoute } from 'vue-router';
import type {
  AdminMarketplaceAssetDeleteImpact,
  AdminMarketplaceAssetDetailView,
  AdminMarketplaceRatingView,
  MarketplaceAnalyticsView,
  MarketplaceCollectionView,
  MarketplaceDemandView,
  MarketplaceDemandDetailView,
  MarketplacePackageView,
  MarketplaceReviewView,
} from 'shared';
import { adminApi } from '@/api';
import AdminUserDetailModal from '@/components/AdminUserDetailModal.vue';
import MarketplaceReviewWorkbench from '@/components/MarketplaceReviewWorkbench.vue';
import MarketplaceAssetReviewDetail from '@/components/MarketplaceAssetReviewDetail.vue';

type Page<T> = { items: T[]; total: number; page: number; limit: number };
const props = defineProps<{
  initialTab?: 'reviews' | 'ratings' | 'demands' | 'assets' | 'curation' | 'analytics';
}>();
const route = useRoute();
const routeTab = (): 'demands' | 'ratings' | undefined =>
  route.query.tab === 'demands' || route.query.tab === 'ratings' ? route.query.tab : undefined;
const tab = ref(routeTab() ?? props.initialTab ?? 'reviews');
const loading = ref(false);
const submitting = ref(false);
const loadError = ref('');
const analytics = ref<MarketplaceAnalyticsView>();
const reviews = ref<MarketplaceReviewView[]>([]);
const ratingItems = ref<AdminMarketplaceRatingView[]>([]);
const demandItems = ref<MarketplaceDemandView[]>([]);
const collections = ref<MarketplaceCollectionView[]>([]);
const assets = ref<MarketplacePackageView[]>([]);
const assetQuery = ref('');
const assetType = ref('');
const assetVisibility = ref('');
const assetPage = ref(1);
const assetPageSize = ref(20);
const assetTotal = ref(0);
let assetSearchTimer: ReturnType<typeof setTimeout> | null = null;
const reviewOpen = ref(false);
const ratingReviewOpen = ref(false);
const workbenchOpen = ref(false);
const assetReviewDetailOpen = ref(false);
const demandReviewOpen = ref(false);
const demandDetailOpen = ref(false);
const demandHideOpen = ref(false);
const collectionOpen = ref(false);
const reviewTarget = ref<MarketplaceReviewView>();
const ratingReviewTarget = ref<AdminMarketplaceRatingView>();
const workbenchTarget = ref<MarketplaceReviewView>();
const assetReviewDetailTarget = ref<MarketplaceReviewView>();
const demandReviewTarget = ref<MarketplaceDemandView>();
const reviewDecision = ref<'approved' | 'rejected'>('approved');
const reason = ref('');
const ratingReviewReason = ref('');
const demandReason = ref('');
const demandDetail = ref<MarketplaceDemandDetailView>();
const demandHideResponseId = ref('');
const demandHideReason = ref('');
const forceUnpublishOpen = ref(false);
const forceUnpublishTarget = ref<MarketplacePackageView>();
const forceUnpublishReason = ref('');
const assetDetailOpen = ref(false);
const assetDetailLoading = ref(false);
const assetDetail = ref<AdminMarketplaceAssetDetailView>();
const assetDeleteOpen = ref(false);
const assetDeleteTarget = ref<MarketplacePackageView>();
const assetDeleteImpact = ref<AdminMarketplaceAssetDeleteImpact>();
const assetDeleteReason = ref('');
const assetDeleteName = ref('');
const userDetailOpen = ref(false);
const userDetailTarget = ref<{ id: string; displayName: string; email?: string }>();
const collectionDraft = ref({
  title: '',
  description: '',
  placement: 'featured' as 'hero' | 'featured' | 'scenario',
  status: 'draft' as 'draft' | 'live',
  packageIds: '',
  reason: '',
});
const pendingReviews = computed(() => reviews.value.filter((item) => item.status === 'pending'));
const pendingRatings = computed(() =>
  ratingItems.value.filter((item) => item.moderationStatus === 'pending'),
);
const pendingDemands = computed(() =>
  demandItems.value.filter((item) => item.status === 'pending'),
);
const funnelCards = computed(() => {
  const funnel = analytics.value?.funnel;
  return [
    { key: 'detail_view', label: '详情浏览', value: funnel?.detail_view ?? 0, note: '进入详情页' },
    {
      key: 'library_added',
      label: '加入资产',
      value: funnel?.library_added ?? 0,
      note: '明确保存意图',
    },
    {
      key: 'install_previewed',
      label: '安装确认',
      value: funnel?.install_previewed ?? 0,
      note: '确认依赖与权限',
    },
    {
      key: 'installation_ready',
      label: '安装就绪',
      value: funnel?.installation_ready ?? 0,
      note: '运行能力可用',
    },
    {
      key: 'activation_completed',
      label: '有效激活',
      value: funnel?.activation_completed ?? 0,
      note: '组件首次实例化',
    },
  ];
});
const reviewColumns = [
  { title: '资产 / 版本', key: 'asset' },
  { title: '提交时间', key: 'submittedAt' },
  { title: '操作', key: 'action' },
];
const demandColumns = [
  { title: '需求', key: 'demand' },
  { title: '提交时间', key: 'submittedAt' },
  { title: '操作', key: 'action' },
];
const ratingColumns = [
  { title: '评价者 / 资产', key: 'author' },
  { title: '评分', key: 'rating' },
  { title: '评价内容', key: 'comment' },
  { title: '提交时间', key: 'submittedAt' },
  { title: '操作', key: 'action' },
];
const collectionColumns = [
  { title: '专题', dataIndex: 'title' },
  { title: '位置', dataIndex: 'placement' },
  { title: '资产', key: 'assets' },
  { title: '状态', key: 'status' },
  { title: '生效时间', key: 'schedule' },
];
const assetColumns = [
  { title: '资产', key: 'asset' },
  { title: '类型', dataIndex: 'type' },
  { title: '上架状态', key: 'status' },
  { title: '审核', key: 'review' },
  { title: '运营优先级', key: 'priority' },
  { title: '操作', key: 'action' },
];
const assetPagination = computed(() => ({
  current: assetPage.value,
  pageSize: assetPageSize.value,
  total: assetTotal.value,
  showSizeChanger: true,
  pageSizeOptions: ['20', '50', '100'],
  showTotal: (total: number) => `共 ${total} 个资产`,
}));
const topColumns = [
  { title: '能力', dataIndex: 'name' },
  { title: '类型', key: 'type' },
  { title: '详情浏览', dataIndex: 'detailViews' },
  { title: '安装就绪', dataIndex: 'readyCount' },
  { title: '有效激活', dataIndex: 'activationCount' },
];
function typeLabel(type?: string): string {
  return (
    (
      { component: '组件', skill: 'Skill', mcp: 'MCP', agent_template: 'Agent 模板' } as Record<
        string,
        string
      >
    )[type ?? ''] ?? '-'
  );
}
function reviewStatus(value?: string): string {
  return (
    (
      {
        approved: '已通过',
        pending: '待审核',
        rejected: '已拒绝',
        not_submitted: '未提交',
      } as Record<string, string>
    )[value ?? ''] ?? '-'
  );
}
function collectionStatus(value: string): string {
  return (
    (
      { draft: '草稿', scheduled: '待生效', live: '生效中', ended: '已结束' } as Record<
        string,
        string
      >
    )[value] ?? value
  );
}
function date(value: string | null): string {
  return value ? new Date(value).toLocaleString('zh-CN', { hour12: false }) : '-';
}
async function loadAssets(): Promise<void> {
  const query = new URLSearchParams({
    page: String(assetPage.value),
    limit: String(assetPageSize.value),
  });
  if (assetQuery.value.trim()) query.set('q', assetQuery.value.trim());
  if (assetType.value) query.set('type', assetType.value);
  if (assetVisibility.value) query.set('visibility', assetVisibility.value);
  query.set('reviewStatus', 'approved');
  const result = await adminApi.get<Page<MarketplacePackageView>>(
    `/admin/marketplace/assets?${query.toString()}`,
  );
  assets.value = result.items;
  assetPage.value = result.page;
  assetPageSize.value = result.limit;
  assetTotal.value = result.total;
}
async function load(): Promise<void> {
  loading.value = true;
  loadError.value = '';
  try {
    const [nextAnalytics, nextReviews, nextRatings, nextDemands, nextCollections] =
      await Promise.all([
        adminApi.get<MarketplaceAnalyticsView>('/admin/marketplace/overview'),
        adminApi.get<Page<MarketplaceReviewView>>(
          '/admin/marketplace/reviews?status=pending&limit=50',
        ),
        adminApi.get<Page<AdminMarketplaceRatingView>>(
          '/admin/marketplace/ratings?status=pending&limit=50',
        ),
        adminApi.get<Page<MarketplaceDemandView>>(
          '/admin/marketplace/demands?status=pending&limit=50',
        ),
        adminApi.get<MarketplaceCollectionView[]>('/admin/marketplace/collections'),
      ]);
    analytics.value = nextAnalytics;
    reviews.value = nextReviews.items;
    ratingItems.value = nextRatings.items;
    demandItems.value = nextDemands.items;
    collections.value = nextCollections;
    await loadAssets();
  } catch {
    loadError.value = '无法加载能力商店运营数据，请确认管理员权限后重试。';
  } finally {
    loading.value = false;
  }
}
function resetAssetPage(): void {
  assetPage.value = 1;
  void loadAssets();
}
function handleAssetTableChange(pagination: { current?: number; pageSize?: number }): void {
  assetPage.value = pagination.current ?? 1;
  assetPageSize.value = pagination.pageSize ?? assetPageSize.value;
  void loadAssets();
}
async function openAssetDetail(target: MarketplacePackageView): Promise<void> {
  assetDetailOpen.value = true;
  assetDetailLoading.value = true;
  assetDetail.value = undefined;
  try {
    assetDetail.value = await adminApi.get<AdminMarketplaceAssetDetailView>(
      `/admin/marketplace/assets/${target.id}`,
    );
  } catch {
    message.error('无法加载资产详情');
    assetDetailOpen.value = false;
  } finally {
    assetDetailLoading.value = false;
  }
}
function openAssetPreview(): void {
  if (!assetDetail.value?.review) return;
  workbenchTarget.value = assetDetail.value.review;
  workbenchOpen.value = true;
}
async function openAssetDelete(target: MarketplacePackageView): Promise<void> {
  assetDeleteTarget.value = target;
  assetDeleteReason.value = '';
  assetDeleteName.value = '';
  assetDeleteImpact.value = undefined;
  assetDeleteOpen.value = true;
  try {
    assetDeleteImpact.value = await adminApi.get<AdminMarketplaceAssetDeleteImpact>(
      `/admin/marketplace/assets/${target.id}/delete-impact`,
    );
  } catch {
    message.error('无法读取删除影响，请刷新后重试');
    assetDeleteOpen.value = false;
  }
}
async function deleteAsset(): Promise<void> {
  if (!assetDeleteTarget.value || !assetDeleteReason.value.trim()) return;
  submitting.value = true;
  try {
    await adminApi.delete(`/admin/marketplace/assets/${assetDeleteTarget.value.id}`, {
      confirmationName: assetDeleteName.value,
      reason: assetDeleteReason.value.trim(),
    });
    message.success('商店资产已永久删除，既有用户副本仍可使用和卸载');
    assetDeleteOpen.value = false;
    assetDetailOpen.value = false;
    if (assets.value.length === 1 && assetPage.value > 1) assetPage.value -= 1;
    await loadAssets();
  } catch {
    message.error('删除失败，请确认资产已下架且名称填写无误');
  } finally {
    submitting.value = false;
  }
}
function openReview(target: MarketplaceReviewView, decision: 'approved' | 'rejected'): void {
  reviewTarget.value = target;
  reviewDecision.value = decision;
  reason.value = '';
  if (decision === 'approved') {
    void submitReview();
    return;
  }
  reviewOpen.value = true;
}
function reviewRating(target: AdminMarketplaceRatingView, decision: 'approved' | 'rejected'): void {
  ratingReviewTarget.value = target;
  ratingReviewReason.value = '';
  if (decision === 'approved') {
    void submitRatingReview('approved');
    return;
  }
  ratingReviewOpen.value = true;
}
async function submitRatingReview(decision: 'approved' | 'rejected' = 'rejected'): Promise<void> {
  if (!ratingReviewTarget.value || (decision === 'rejected' && !ratingReviewReason.value.trim()))
    return;
  submitting.value = true;
  try {
    await adminApi.post(`/admin/marketplace/ratings/${ratingReviewTarget.value.id}/decision`, {
      decision,
      ...(decision === 'rejected' ? { reason: ratingReviewReason.value.trim() } : {}),
    });
    message.success(decision === 'approved' ? '评价已通过并公开展示' : '评价已拒绝，不会公开展示');
    ratingReviewOpen.value = false;
    await load();
  } catch {
    message.error('评价审核提交失败，请刷新后重试');
  } finally {
    submitting.value = false;
  }
}
function openReviewDetail(target: MarketplaceReviewView): void {
  if (target.release?.manifest.type === 'component') {
    workbenchTarget.value = target;
    workbenchOpen.value = true;
    return;
  }
  assetReviewDetailTarget.value = target;
  assetReviewDetailOpen.value = true;
}
async function submitReview(): Promise<void> {
  if (!reviewTarget.value || (reviewDecision.value === 'rejected' && !reason.value.trim())) return;
  submitting.value = true;
  try {
    await adminApi.post(`/admin/marketplace/reviews/${reviewTarget.value.id}/decision`, {
      decision: reviewDecision.value,
      ...(reviewDecision.value === 'rejected' ? { reason: reason.value.trim() } : {}),
    });
    message.success(
      reviewDecision.value === 'approved' ? '已通过审核并上线该版本' : '已拒绝该版本',
    );
    reviewOpen.value = false;
    await load();
  } catch {
    message.error('审核提交失败，请稍后重试');
  } finally {
    submitting.value = false;
  }
}
function reviewDemand(target: MarketplaceDemandView, decision: 'approved' | 'rejected'): void {
  demandReviewTarget.value = target;
  demandReason.value = '';
  if (decision === 'approved') {
    void submitDemandReview('approved');
    return;
  }
  demandReviewOpen.value = true;
}
async function submitDemandReview(decision: 'approved' | 'rejected' = 'rejected'): Promise<void> {
  if (!demandReviewTarget.value || (decision === 'rejected' && !demandReason.value.trim())) return;
  submitting.value = true;
  try {
    await adminApi.post(`/admin/marketplace/demands/${demandReviewTarget.value.id}/decision`, {
      decision,
      ...(decision === 'rejected' ? { reason: demandReason.value.trim() } : {}),
    });
    message.success(decision === 'approved' ? '需求已通过审核并公开' : '需求已拒绝');
    demandReviewOpen.value = false;
    await load();
  } catch {
    message.error('需求审核提交失败，请稍后重试');
  } finally {
    submitting.value = false;
  }
}
async function openDemandDetail(demandId: string): Promise<void> {
  try {
    demandDetail.value = await adminApi.get<MarketplaceDemandDetailView>(
      `/admin/marketplace/demands/${demandId}`,
    );
    demandDetailOpen.value = true;
  } catch {
    message.error('无法加载需求详情');
  }
}
function openUserDetail(user: { id: string; displayName: string; email?: string }): void {
  userDetailTarget.value = user;
  userDetailOpen.value = true;
}
function openHideResponse(responseId: string): void {
  demandHideResponseId.value = responseId;
  demandHideReason.value = '';
  demandHideOpen.value = true;
}
async function hideDemandResponse(): Promise<void> {
  if (!demandDetail.value || !demandHideResponseId.value || !demandHideReason.value.trim()) return;
  submitting.value = true;
  try {
    await adminApi.post(
      `/admin/marketplace/demands/${demandDetail.value.id}/responses/${demandHideResponseId.value}/hide`,
      { reason: demandHideReason.value.trim() },
    );
    message.success('响应已隐藏');
    demandHideOpen.value = false;
    await openDemandDetail(demandDetail.value.id);
  } catch {
    message.error('隐藏响应失败，请稍后重试');
  } finally {
    submitting.value = false;
  }
}
function openForceUnpublish(target: MarketplacePackageView): void {
  forceUnpublishTarget.value = target;
  forceUnpublishReason.value = '';
  forceUnpublishOpen.value = true;
}
async function forceUnpublish(): Promise<void> {
  if (!forceUnpublishTarget.value || !forceUnpublishReason.value.trim()) return;
  submitting.value = true;
  try {
    await adminApi.post(
      `/admin/marketplace/assets/${forceUnpublishTarget.value.id}/force-unpublish`,
      { reason: forceUnpublishReason.value.trim() },
    );
    message.success('资产已强制下架');
    forceUnpublishOpen.value = false;
    await load();
  } catch {
    message.error('强制下架失败，请确认资产当前状态与管理员权限');
  } finally {
    submitting.value = false;
  }
}
async function saveCollection(): Promise<void> {
  const packageIds = collectionDraft.value.packageIds
    .split(/\s|,/)
    .map((value) => value.trim())
    .filter(Boolean);
  submitting.value = true;
  try {
    await adminApi.post('/admin/marketplace/collections', {
      ...collectionDraft.value,
      packageIds,
      category: null,
      startsAt: null,
      endsAt: null,
    });
    message.success('专题已保存');
    collectionOpen.value = false;
    collectionDraft.value = {
      title: '',
      description: '',
      placement: 'featured',
      status: 'draft',
      packageIds: '',
      reason: '',
    };
    await load();
  } catch {
    message.error('专题保存失败，请检查资产是否已审核通过，以及主推荐位是否冲突');
  } finally {
    submitting.value = false;
  }
}
async function updatePriority(packageId: string, priority: number): Promise<void> {
  try {
    await adminApi.patch(`/admin/marketplace/assets/${packageId}/priority`, {
      priority,
      expiresAt: null,
      reason: '运营排序调整',
    });
    message.success('运营优先级已更新');
    await loadAssets();
  } catch {
    message.error('优先级更新失败，只能调整已审核通过的在线资产');
  }
}
watch([assetQuery, assetType, assetVisibility], () => {
  if (assetSearchTimer) clearTimeout(assetSearchTimer);
  assetSearchTimer = setTimeout(() => {
    assetPage.value = 1;
    void loadAssets();
  }, 300);
});
watch(
  () => props.initialTab,
  (value) => {
    tab.value = routeTab() ?? value ?? 'reviews';
  },
);
watch(
  () => route.query.tab,
  () => {
    tab.value = routeTab() ?? props.initialTab ?? 'reviews';
  },
);
onMounted(() => {
  void load();
});
</script>

<style scoped>
.operations-page {
  min-height: 100%;
  padding: 0;
}
.page-header {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: center;
  max-width: 1320px;
  margin: 0 auto 20px;
}
.page-header span {
  display: block;
  color: #777b90;
  font-size: 13px;
  line-height: 1.5;
}
.error {
  max-width: 1320px;
  margin: 0 auto 18px;
}
.funnel {
  display: grid;
  max-width: 1320px;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin: 0 auto 20px;
}
.funnel article,
.panel {
  border: 1px solid #e7e5ee;
  border-radius: 14px;
  background: #fff;
}
.funnel article {
  padding: 17px;
}
.funnel span,
.funnel small {
  display: block;
  color: #8b8897;
  font-size: 12px;
}
.funnel strong {
  display: block;
  margin: 8px 0;
  color: #3b3650;
  font-size: 27px;
}
.marketplace-tabs {
  max-width: 1320px;
  margin: 0 auto;
}
.marketplace-tabs :deep(.ant-tabs-nav) {
  margin-bottom: 16px;
  padding: 0 4px;
}
.marketplace-tabs :deep(.ant-tabs-content-holder) {
  min-width: 0;
}
.panel {
  padding: 20px;
}
.panel-head {
  display: flex;
  justify-content: space-between;
  align-items: start;
  gap: 20px;
  margin-bottom: 17px;
}
.panel h2 {
  margin: 0;
  color: #39354d;
  font-size: 18px;
}
.panel p {
  margin: 6px 0 0;
  color: #898696;
  font-size: 12px;
}
.panel small {
  display: block;
  color: #898696;
  font-size: 11px;
}
.user-link {
  height: auto;
  padding: 0;
}
.asset-search {
  width: 260px;
}
.asset-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}
.asset-filter {
  width: 150px;
}
.asset-name {
  height: auto;
  padding: 0;
  font-weight: 600;
  text-align: left;
}
.asset-detail-head {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 18px;
}
.asset-detail-head img {
  width: 48px;
  height: 48px;
  border: 1px solid var(--aw-border);
  border-radius: var(--aw-radius-sm);
  object-fit: cover;
}
.asset-detail-head h2,
.asset-detail-head p,
.asset-detail-section p {
  margin: 0;
}
.asset-detail-head p,
.asset-detail-section p,
.delete-impact {
  color: var(--aw-text-secondary);
  line-height: 1.65;
}
.asset-detail-section {
  margin: 20px 0;
}
.asset-detail-section h3 {
  margin: 0 0 8px;
  font-size: 14px;
}
.asset-screenshots {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-top: 12px;
}
.asset-screenshots img {
  width: 100%;
  border: 1px solid var(--aw-border);
  border-radius: var(--aw-radius-sm);
}
.demand-detail-copy {
  color: #5e5b68;
  line-height: 1.65;
  white-space: pre-wrap;
}
.demand-response-list {
  display: grid;
  gap: 10px;
}
.demand-response-list article {
  padding: 12px;
  border: 1px solid #e7e5ee;
  border-radius: 8px;
}
.demand-response-list article > p {
  margin: 8px 0;
  color: #5e5b68;
  white-space: pre-wrap;
}
@media (max-width: 800px) {
  .page-header,
  .panel-head {
    align-items: flex-start;
    flex-direction: column;
  }
  .funnel {
    grid-template-columns: repeat(2, 1fr);
  }
  .marketplace-tabs :deep(.ant-tabs-nav) {
    overflow-x: auto;
    padding: 0;
  }
  .asset-search {
    width: 100%;
  }
  .asset-filter {
    flex: 1 1 145px;
  }
}
@media (max-width: 440px) {
  .funnel {
    grid-template-columns: 1fr;
  }
}
</style>
